import useWindowDimensions from '@hooks/useWindowDimensions';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';

// Journey Section Skeleton
export const JourneySectionSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        borderRadius: 8,
        p: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Skeleton variant="text" width={150} height={32} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={250} height={20} />
        </Box>
        <Skeleton
          variant="rectangular"
          width={120}
          height={44}
          sx={{ borderRadius: 10, bgcolor: '#60a5fa' }}
        />
      </Box>

      {/* Journey Cards Grid */}
      <Grid container spacing={3}>
        {/* Engagement Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              height: '100%',
              bgcolor: 'white',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              {/* Title */}
              <Skeleton variant="text" width="70%" height={24} sx={{ mx: 'auto', mb: 3 }} />

              {/* Streak Badge */}
              <Box display="flex" justifyContent="center" mb={2}>
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={28}
                  sx={{ borderRadius: 2, bgcolor: '#fbbf24' }}
                />
              </Box>

              {/* Large Circle with Number */}
              <Box position="relative" display="inline-flex" mb={2}>
                <Skeleton variant="circular" width={100} height={100} sx={{ bgcolor: '#fcd34d' }} />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{ transform: 'translate(-50%, -50%)' }}
                >
                  <Skeleton variant="text" width={40} height={32} />
                </Box>
              </Box>

              {/* Description */}
              <Skeleton variant="text" width="90%" height={16} sx={{ mx: 'auto', mb: 2 }} />

              {/* Level Badge */}
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={70} height={16} />
              </Box>

              {/* View Progress Link */}
              <Skeleton variant="text" width={100} height={16} sx={{ mx: 'auto' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Learning Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              height: '100%',
              bgcolor: 'white',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              {/* Title */}
              <Skeleton variant="text" width="60%" height={24} sx={{ mx: 'auto', mb: 3 }} />

              {/* Large Circle with Number */}
              <Box position="relative" display="inline-flex" mb={2} mt={4}>
                <Skeleton variant="circular" width={100} height={100} sx={{ bgcolor: '#fcd34d' }} />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{ transform: 'translate(-50%, -50%)' }}
                >
                  <Skeleton variant="text" width={35} height={32} />
                </Box>
              </Box>

              {/* Description */}
              <Skeleton variant="text" width="80%" height={16} sx={{ mx: 'auto', mb: 4 }} />

              {/* View Progress Link */}
              <Skeleton variant="text" width={100} height={16} sx={{ mx: 'auto' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Actions Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              height: '100%',
              bgcolor: 'white',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              {/* Title */}
              <Skeleton variant="text" width="55%" height={24} sx={{ mx: 'auto', mb: 3 }} />

              {/* Large Circle with Number */}
              <Box position="relative" display="inline-flex" mb={2} mt={4}>
                <Skeleton variant="circular" width={100} height={100} sx={{ bgcolor: '#fcd34d' }} />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{ transform: 'translate(-50%, -50%)' }}
                >
                  <Skeleton variant="text" width={20} height={32} />
                </Box>
              </Box>

              {/* Description */}
              <Skeleton variant="text" width="85%" height={16} sx={{ mx: 'auto', mb: 4 }} />

              {/* View Progress Link */}
              <Skeleton variant="text" width={100} height={16} sx={{ mx: 'auto' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Suggested Next Action Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 8,
              border: '1px solid #c4b5fd',
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.15)',
              height: '100%',
              bgcolor: '#f3f4f6',
              background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              {/* Title */}
              <Skeleton
                variant="text"
                width="90%"
                height={24}
                sx={{ mx: 'auto', mb: 4, bgcolor: 'rgba(139, 92, 246, 0.3)' }}
              />

              {/* Centered Content Area */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: 120 }}
              >
                {/* Action Title */}
                <Skeleton
                  variant="text"
                  width="100%"
                  height={28}
                  sx={{ bgcolor: 'rgba(139, 92, 246, 0.4)' }}
                />
                <Skeleton
                  variant="text"
                  width="75%"
                  height={28}
                  sx={{ bgcolor: 'rgba(139, 92, 246, 0.4)' }}
                />
                <Skeleton
                  variant="text"
                  width="50%"
                  height={28}
                  sx={{ bgcolor: 'rgba(139, 92, 246, 0.4)' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Wellbeing Trends Skeleton
export const WellbeingTrendsSkeleton: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Card
      sx={{
        height: 347,
        borderRadius: 8,
        width: isDesktop ? '50%' : '100%',
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Skeleton variant="text" width={200} height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={140} height={18} />
          </Box>
          <Skeleton
            variant="rectangular"
            width={90}
            height={40}
            sx={{ borderRadius: 10, bgcolor: '#60a5fa' }}
          />
        </Box>

        {/* Chart area */}
        <Box sx={{ height: 160, position: 'relative', mb: 3, mt: 3 }}>
          {/* Y-axis labels */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 20,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              pr: 1,
            }}
          >
            {[10, 8, 6, 4, 2, 0].map((value, index) => (
              <Skeleton key={index} variant="text" width={12} height={16} />
            ))}
          </Box>

          {/* Chart plotting area */}
          <Box sx={{ ml: 3, height: '100%', position: 'relative' }}>
            {/* Chart background grid lines */}
            {[0, 20, 40, 60, 80, 100].map((position, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  // top: `${position}%`,
                  left: 0,
                  right: 0,
                  height: 1,
                  bgcolor: '#f3f4f6',
                  opacity: 0.5,
                }}
              />
            ))}

            {/* Chart line */}
            <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
              {/* Line path */}
              <path
                d="M 0 50 Q 80 48 160 42 T 320 45 Q 400 52 480 55"
                stroke="#6366f1"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Data points */}
              {[
                { x: 0, y: 50 },
                { x: 120, y: 48 },
                { x: 240, y: 42 },
                { x: 360, y: 45 },
                { x: 480, y: 55 },
              ].map((point, index) => (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  fill="#6366f1"
                  stroke="white"
                  strokeWidth="2"
                />
              ))}
            </svg>
          </Box>
        </Box>

        {/* X-axis labels */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ ml: 3, mb: 2 }}
        >
          {['Jan 5', 'Jan 12', 'Jan 19', 'Jan 26'].map((date, index) => (
            <Skeleton key={index} variant="text" width={40} height={16} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// Journal Section Skeleton
export const JournalSectionSkeleton: React.FC = () => {
  const { isDesktop } = useWindowDimensions();

  return (
    <Card
      sx={{
        height: 347,
        borderRadius: 8,
        width: isDesktop ? '50%' : '100%',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Skeleton variant="text" width="50%" height={24} />
          <Skeleton
            variant="rectangular"
            width={90}
            height={40}
            sx={{ borderRadius: 5, bgcolor: '#60a5fa' }}
          />
        </Box>

        {/* Journal entries */}
        {[...Array(3)].map((_, index) => (
          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Skeleton variant="text" width="30%" height={16} />
              <Skeleton variant="text" width="20%" height={16} />
            </Box>
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="75%" height={20} />
          </Box>
        ))}

        <Box textAlign="center" mt={2}>
          <Skeleton
            variant="rectangular"
            width={120}
            height={36}
            sx={{ mx: 'auto', borderRadius: 1 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Wellbeing Profile Skeleton
export const WellbeingProfileSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Card
      sx={{
        borderRadius: 8,
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header with date, title and percentage */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Skeleton variant="text" width={160} height={16} sx={{ mb: 1 }} />
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width={180} height={28} />
            </Box>
          </Box>
          <Skeleton variant="text" width={40} height={28} />
        </Box>

        {/* Progress bar */}
        <Box mb={4}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={14}
            sx={{
              borderRadius: 1,
              background: 'linear-gradient(90deg, #329AFB 0%, #329AFB 50%, #F000A8 100%)',
            }}
          />
        </Box>

        {/* Wellbeing snapshot section */}
        <Box mb={4}>
          <Skeleton variant="text" width={160} height={24} sx={{ mb: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Skeleton variant="text" width="100%" height={18} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="95%" height={18} />
          </Box>
        </Box>

        {/* Wellbeing style section */}
        <Box mb={4}>
          <Skeleton variant="text" width={140} height={24} sx={{ mb: 2 }} />

          <Box>
            <Skeleton variant="text" width="100%" height={18} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={18} />
          </Box>
        </Box>

        {/* Patterns section */}
        <Box mb={4}>
          <Skeleton variant="text" width={80} height={24} sx={{ mb: 3 }} />

          {[...Array(3)].map((_, index) => (
            <Box key={index} display="flex" alignItems="flex-start" gap={2} mb={2}>
              <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: '#22c55e' }} />
              <Skeleton variant="text" width="90%" height={18} />
            </Box>
          ))}
        </Box>

        {/* Your boosters section */}
        <Box mb={4}>
          <Skeleton variant="text" width={120} height={24} sx={{ mb: 2 }} />

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {[{ width: 120 }, { width: 80 }, { width: 140 }, { width: 100 }, { width: 90 }].map(
              (item, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={item.width}
                  height={32}
                  sx={{
                    borderRadius: 5,
                    bgcolor: 'rgba(34, 197, 94, 0.2)',
                    mb: 1,
                  }}
                />
              ),
            )}
          </Stack>
        </Box>

        {/* Your drainers section */}
        <Box mb={4}>
          <Skeleton variant="text" width={120} height={24} sx={{ mb: 2 }} />

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {[{ width: 110 }, { width: 100 }, { width: 110 }, { width: 120 }, { width: 110 }].map(
              (item, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={item.width}
                  height={32}
                  sx={{
                    borderRadius: 5,
                    bgcolor: 'rgba(239, 68, 68, 0.2)',
                    mb: 1,
                  }}
                />
              ),
            )}
          </Stack>
        </Box>

        {/* Your focus areas section */}
        <Box mb={5}>
          <Skeleton variant="text" width={140} height={24} sx={{ mb: 2 }} />

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {[{ width: 110 }, { width: 100 }, { width: 110 }].map((item, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width={item.width}
                height={32}
                sx={{
                  borderRadius: 5,
                  bgcolor: 'rgba(156, 163, 175, 0.2)',
                  mb: 1,
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Recommended Next Action section */}
        <Box
          textAlign="center"
          sx={{
            bgcolor: '#f9fafb',
            borderRadius: 2,
            p: 3,
            mb: 4,
            border: '1px solid #f3f4f6',
          }}
        >
          <Skeleton variant="circular" width={40} height={40} sx={{ mx: 'auto', mb: 2 }} />
          <Skeleton variant="text" width={200} height={24} sx={{ mx: 'auto', mb: 2 }} />
          <Skeleton variant="text" width={280} height={18} sx={{ mx: 'auto', mb: 3 }} />
          <Skeleton
            variant="rectangular"
            width={180}
            height={44}
            sx={{ mx: 'auto', borderRadius: 10, bgcolor: '#60a5fa' }}
          />
        </Box>

        {/* Core pillar scores section */}
        <Box>
          <Skeleton variant="text" width={140} height={24} sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            {[
              { name: 'holistic', color: '#fbbf24', unlocked: true },
              { name: 'mental', color: '#9ca3af', unlocked: false },
              { name: 'physical', color: '#a855f7', unlocked: true },
              { name: 'financial', color: '#9ca3af', unlocked: false },
              { name: 'social', color: '#9ca3af', unlocked: false },
              { name: 'career', color: '#9ca3af', unlocked: false },
            ].map((pillar, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <Card
                  sx={{
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    bgcolor: pillar.unlocked
                      ? pillar.color === '#fbbf24'
                        ? '#fef3c7'
                        : '#f3f4f6'
                      : '#f9fafb',
                    textAlign: 'center',
                    p: 2,
                    minHeight: 140,
                  }}
                >
                  <Box mb={2}>
                    <Skeleton
                      variant="circular"
                      width={32}
                      height={32}
                      sx={{ mx: 'auto', mb: 1, bgcolor: pillar.color }}
                    />
                    <Skeleton variant="text" width={60} height={16} sx={{ mx: 'auto' }} />
                  </Box>

                  {pillar.unlocked ? (
                    <Box>
                      <Box position="relative" display="inline-flex" mb={2}>
                        <Skeleton
                          variant="circular"
                          width={50}
                          height={50}
                          sx={{ bgcolor: pillar.color === '#fbbf24' ? '#fbbf24' : '#a855f7' }}
                        />
                        <Box
                          position="absolute"
                          top="50%"
                          left="50%"
                          sx={{ transform: 'translate(-50%, -50%)' }}
                        >
                          <Skeleton variant="text" width={20} height={20} />
                          <Skeleton variant="text" width={25} height={12} />
                        </Box>
                      </Box>
                      <Skeleton variant="text" width={80} height={14} sx={{ mx: 'auto' }} />
                    </Box>
                  ) : (
                    <Box>
                      <Skeleton
                        variant="circular"
                        width={50}
                        height={50}
                        sx={{ mx: 'auto', mb: 2, bgcolor: '#9ca3af' }}
                      />
                      <Skeleton variant="text" width={90} height={14} sx={{ mx: 'auto' }} />
                    </Box>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

// Wellbeing Indicators Skeleton
export const WellbeingIndicatorsSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Card
      sx={{
        borderRadius: 8,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: { xs: 2, md: 3 },
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header with title and edit button */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Skeleton variant="text" width={200} height={28} />
          <Skeleton variant="rectangular" width={60} height={36} sx={{ borderRadius: 3 }} />
        </Box>

        {/* Description */}
        <Skeleton variant="text" width="85%" height={18} sx={{ mb: 4 }} />

        {/*  cards */}
        <Grid container spacing={2}>
          {[
            {
              category: 'Mental',
              title: 'Stress Resilience',
              color: 'rgba(34, 197, 94, 0.2)',
              iconColor: '#22c55e',
            },
            {
              category: 'Mental',
              title: 'Managing Emotions',
              color: 'rgba(34, 197, 94, 0.2)',
              iconColor: '#22c55e',
            },
            {
              category: 'Financial',
              title: 'Spending',
              color: 'rgba(249, 115, 22, 0.2)',
              iconColor: '#f97316',
            },
            {
              category: 'Mental 2',
              title: 'Stress Resilience',
              color: 'rgba(34, 197, 94, 0.2)',
              iconColor: '#22c55e',
            },
            {
              category: 'Mental 3',
              title: 'Managing Emotions',
              color: 'rgba(34, 197, 94, 0.2)',
              iconColor: '#22c55e',
            },
            {
              category: 'Financial 4',
              title: 'Spending',
              color: 'rgba(249, 115, 22, 0.2)',
              iconColor: '#f97316',
            },
          ].map((priority, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.8)',
                  border: '1px solid rgba(0,0,0,0.1)',
                  position: 'relative',
                  minHeight: 120,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  {/* Close button */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  >
                    <Skeleton variant="circular" width={20} height={20} />
                  </Box>

                  {/* Category with icon */}
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Skeleton
                      variant="circular"
                      width={16}
                      height={16}
                      sx={{ bgcolor: priority.iconColor }}
                    />
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={20} height={16} />
                    <Skeleton variant="text" width={60} height={16} />
                  </Box>

                  {/*  title */}
                  <Skeleton variant="text" width="85%" height={24} sx={{ fontWeight: 600 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

// Wellbeing Plan Skeleton
export const WellbeingPlanSkeleton: React.FC = () => (
  <Card sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
    <CardContent sx={{ p: { xs: 2, md: 4 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
      </Box>

      {[...Array(3)].map((_, index) => (
        <Card key={index} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box flex={1}>
                <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
              <Skeleton
                variant="rectangular"
                width={80}
                height={32}
                sx={{ borderRadius: 1, ml: 2, bgcolor: '#beaaf3' }}
              />
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" gap={1}>
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={24}
                  sx={{ borderRadius: 1, bgcolor: '#04bc00' }}
                />
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={24}
                  sx={{ borderRadius: 1, bgcolor: '#04bc00' }}
                />
              </Box>
              <Skeleton variant="text" width={80} height={16} />
            </Box>
          </CardContent>
        </Card>
      ))}

      <Box textAlign="center" mt={3}>
        <Skeleton
          variant="rectangular"
          width={150}
          height={40}
          sx={{ mx: 'auto', borderRadius: 5, bgcolor: '#60a5fa' }}
        />
      </Box>
    </CardContent>
  </Card>
);
